import React, { useEffect, useMemo, useRef, useState } from 'react';
import './messages.css';

async function jsonFetch(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload.ok === false) {
    throw new Error(payload.error || response.statusText);
  }
  return payload;
}

const fullPatientName = (c) =>
  (c.patient?.first_name || c.patient?.last_name)
    ? `${c.patient?.first_name ?? ''} ${c.patient?.last_name ?? ''}`.trim()
    : c.patient_identifier;


async function getConversations({ apiBase, clinician_uuid, limit = 50 }) {
  const qs = new URLSearchParams({ clinician_uuid, limit: String(limit) }).toString();
  const result = await jsonFetch(
    await fetch(`${apiBase}/Clinician/conversations?${qs}`)
  );
  return result.conversations || [];
}

async function createOrGetConversation({ apiBase, patient_identifier, clinician_uuid }) {
  const result = await jsonFetch(
    await fetch(`${apiBase}/Clinician/conversations`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ patient_identifier, clinician_uuid }),
    })
  );
  return result.conversation;
}

async function listMessages({ apiBase, convo_uuid, since_id }) {
  const params = { limit: '200' };
  if (since_id) params.since_id = String(since_id);
  const qs = new URLSearchParams(params).toString();
  const result = await jsonFetch(
    await fetch(`${apiBase}/Clinician/conversations/${convo_uuid}/messages?${qs}`)
  );
  return result.messages || [];
}

async function sendMessage({ apiBase, convo_uuid, body, clinician_identifier }) {
  const result = await jsonFetch(
    await fetch(`${apiBase}/Clinician/conversations/${convo_uuid}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sender_type: 'clinician',
        sender_identifier: clinician_identifier,
        body,
      }),
    })
  );
  return result.message;
}

async function markRead({ apiBase, convo_uuid }) {
  await jsonFetch(
    await fetch(`${apiBase}/Clinician/conversations/${convo_uuid}/mark-as-read`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reader_type: 'clinician' }),
    })
  );
}

export function MessageThread({ clinician_uuid, clinician_identifier, patient_identifier, apiBase }) {
  const [loading, setLoading] = useState(true);
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const lastMessageIdRef = useRef(null);
  const timerRef = useRef(null);
  const chatEndRef = useRef(null);

  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const conversation = await createOrGetConversation({ apiBase, patient_identifier, clinician_uuid });
        if (!alive) return;

        setConvo(conversation);
        await markRead({ apiBase, convo_uuid: conversation.convo_uuid });

        const initialMessages = await listMessages({ apiBase, convo_uuid: conversation.convo_uuid });
        if (!alive) return;

        setMessages(initialMessages);
        lastMessageIdRef.current = initialMessages.at(-1)?.id ?? null;

        timerRef.current = setInterval(async () => {
          try {
            if (!document.hidden) {
              const more = await listMessages({
                apiBase,
                convo_uuid: conversation.convo_uuid,
                since_id: lastMessageIdRef.current,
              });
              if (more.length) {
                setMessages((prev) => [...prev, ...more]);
                lastMessageIdRef.current = more.at(-1)?.id ?? lastMessageIdRef.current;
              }
            }
          } catch (err) {
            console.error('Failed to refresh messages:', err);
          }
        }, 3000);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
      clearInterval(timerRef.current);
    };
  }, [apiBase, clinician_uuid, patient_identifier]);

  const handleSend = async () => {
    if (!convo || !text.trim()) return;

    const body = text.trim();
    const tempId = `temp_${Date.now()}`;
    const draft = { id: tempId, body, sender_type: 'clinician' };

    setMessages((prev) => [...prev, draft]);
    setText('');

    try {
      const confirmed = await sendMessage({
        apiBase,
        convo_uuid: convo.convo_uuid,
        body,
        clinician_identifier,
      });
      setMessages((prev) => prev.map((msg) => (msg.id === tempId ? confirmed : msg)));
      lastMessageIdRef.current = confirmed.id;
    } catch (err) {
      console.error('Failed to send message:', err);
      setMessages((prev) => prev.filter((msg) => msg.id !== tempId));
    }
  };

  const onKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return <div className="clinician-pad">Loading Conversation...</div>;
  }

  return (
    <div className="clinician-thread">
      <div className="clinician-chat">
        {messages.map((message) => (
          <div
            key={message.id}
            className={`clinician-bubble-wrapper ${message.sender_type === 'clinician' ? 'me' : 'them'}`}
          >
            <div className="clinician-bubble">{message.body}</div>
          </div>
        ))}
        <div ref={chatEndRef} />
      </div>
      <div className="clinician-chat-input-area">
        <input
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyPress={onKeyPress}
          placeholder="Type a message..."
          className="clinician-chat-input"
        />
        <button className="clinician-btn-primary" onClick={handleSend}>
          Send
        </button>
      </div>
    </div>
  );
}

export default function MessagesTab({ clinician_uuid, clinician_identifier, apiBase }) {
  const [loading, setLoading] = useState(true);
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);

  const loadConversations = useMemo(
    () =>
      async () => {
        const rows = await getConversations({ apiBase, clinician_uuid });
        setConversations(rows);
      },
    [apiBase, clinician_uuid]
  ); // ADD CLINICIAN REFERENCE UUID

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await loadConversations();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [loadConversations]);

  return (
    <div className="clinician-content-split">
      <div className="clinician-sidebar">
        <div className="clinician-sidebar-header">
          <div className="clinician-title"> 

            fdsfsdfsdfsd
          </div>
        </div>
        {loading ? (
          <div className="clinician-pad">Loading Conversations...</div>
        ) : (
        <div className="clinician-list">
  {conversations.map((c) => {
    const name = fullPatientName(c);
    const pid  = c.patient?.patient_id; // may be null
    const when = new Date(c.last_message_at || c.created_at).toLocaleString();

    return (
      <div
        key={c.convo_uuid}
        className={`clinician-list-item ${selected?.patient_identifier === c.patient_identifier ? 'selected' : ''}`}
        onClick={() => setSelected({ patient_identifier: c.patient_identifier })}
      >
        <div className="clinician-list-item-line1">
          {name}
          {pid ? <span className="clinician-pill" title="PatientID">{pid}</span> : null}
        </div>
        <div className="clinician-list-item-line2">{when}</div>
      </div>
    );
  })}
</div>

        )}
      </div>
      <div className="clinician-main-content">
        {selected ? (
          <MessageThread
            clinician_uuid={clinician_uuid}
            clinician_identifier={clinician_identifier}
            patient_identifier={selected.patient_identifier}
            apiBase={apiBase}
          />
        ) : (
          <div className="clinician-empty-state">Select a conversation to begin</div>
        )}
      </div>
    </div>
  );
}
